import CompanionIcon from "@assets/imgs/companion-icon.svg";
import CompanionLogo from "@assets/imgs/companion.png";
import { CompanionV2Chat } from "@components/companion-v2/CompanionV2Chat";
import { TreatBalanceIndicator } from "@components/companion-v2/TreatBalanceIndicator";
import {
  faHistory,
  faGear,
  faGripLinesVertical,
} from "@fortawesome/free-solid-svg-icons";
import {
  faBell,
  faMessageSmile,
  faQuestion,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAuth, useVisibleNotifications } from "@hooks";
import {
  AvailableCompanions,
  AvailableCompanionTabs,
  AvailableSettingsSections,
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_MIN_WIDTH,
  getDefaultWidth,
  useCompanion,
} from "@hooks/use-companion";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useRef, useState } from "react";

import { CompanionNavButton, NewSessionButton } from "./Buttons";
import { CompanionDownstate } from "./CompanionDownstate";
import CompanionHelp from "./CompanionHelp";
import { CompanionModalContainerProvider } from "./CompanionModalContainerContext";
import CompanionNotifications from "./CompanionNotifications";
import { CompanionSessionChat } from "./CompanionSessionChat";
import { CompanionSessionHistory } from "./CompanionSessionHistory";
import CompanionSettings from "./CompanionSettings";
import { CompanionTooltips } from "./CompanionTooltips";

export const CompanionSidebar = () => {
  const SNAP_DISTANCE = 128; // snap to min width if the drag lands near edge

  // State
  const [dragging, setDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(null);
  const [containerElement, setContainerElement] = useState(null);

  // Refs
  const handleRef = useRef(null); // the draggable grip
  const dragOccurredRef = useRef(false);

  const {
    companionSession,
    setCompanionSession,
    activeTab,
    setActiveTab,
    setSettingsSection,
    sidebarWidth: width,
    setSidebarWidth: setWidth,
  } = useCompanion();
  const { enableGlobalCompanion } = useFlags();
  const companionAvailable = enableGlobalCompanion;

  const handleTreatsClick = () => {
    setSettingsSection(AvailableSettingsSections.billing);
    setActiveTab(AvailableCompanionTabs.settings);
  };
  const { isUserAuthenticated } = useAuth();

  const { visible: companionNotifications } = useVisibleNotifications(
    {
      domain: "companion",
    },
  );
  const hasCompanionNotifications = companionNotifications.length > 0;
  const companionUnreadCount = companionNotifications.filter(
    (n) => !n.readAt,
  ).length;
  const isNotificationsTabActive =
    activeTab === AvailableCompanionTabs.notifications;
  const showNotificationsButton =
    hasCompanionNotifications || isNotificationsTabActive;
  const isCollapsed = width <= SIDEBAR_MIN_WIDTH;
  const SIDEBAR_WORDMARK_MIN_WIDTH = 520;
  const isNarrow = !isCollapsed && width < SIDEBAR_WORDMARK_MIN_WIDTH;

  // Auto-open sidebar on first visit (no stored width yet)
  useEffect(() => {
    if (
      isUserAuthenticated &&
      localStorage.getItem("companionSidebarWidth") === null
    ) {
      setWidth(getDefaultWidth());
    }
  }, [isUserAuthenticated, setWidth]);

  const onHandlePointerDown = (e) => {
    dragOccurredRef.current = false;
    setDragStartX(e.clientX);
  };

  // Effects
  useEffect(() => {
    const onWindowResize = () => {
      setWidth((w) =>
        Math.max(
          SIDEBAR_COLLAPSED_WIDTH,
          Math.min(w, window.innerWidth),
        ),
      );
    };

    window.addEventListener("resize", onWindowResize);
    return () => window.removeEventListener("resize", onWindowResize);
  }, [setWidth]);

  useEffect(() => {
    if (!dragging && !dragStartX) return;

    const onDragMove = (ev) => {
      if (!containerElement) return;
      ev.preventDefault();
      const left = containerElement.getBoundingClientRect().left || 0;
      setWidth(
        Math.max(
          SIDEBAR_COLLAPSED_WIDTH,
          Math.min(ev.clientX - left, window.innerWidth),
        ),
      );
      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";
    };

    const onDragEnd = () => {
      if (dragging) {
        dragOccurredRef.current = true;
        setWidth((current) => {
          let snap = current;
          if (current <= SIDEBAR_MIN_WIDTH + SNAP_DISTANCE) {
            snap = SIDEBAR_COLLAPSED_WIDTH;
          } else if (
            document.body.clientWidth < document.body.scrollWidth
          ) {
            snap = window.innerWidth;
          }
          return snap;
        });
      }

      setDragging(false);
      setDragStartX(null);

      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };

    const onHandlePointerMove = (e) => {
      if (dragStartX === null) return;

      const deltaX = Math.abs(e.clientX - dragStartX);
      if (deltaX > 5 && !dragging) {
        setDragging(true);
        handleRef.current?.setPointerCapture?.(e.pointerId);
      }
    };

    document.addEventListener("pointermove", onDragMove, {
      passive: false,
    });
    document.addEventListener("pointermove", onHandlePointerMove);
    document.addEventListener("pointerup", onDragEnd);
    document.addEventListener("pointercancel", onDragEnd);

    return () => {
      document.removeEventListener("pointermove", onDragMove);
      document.removeEventListener(
        "pointermove",
        onHandlePointerMove,
      );
      document.removeEventListener("pointerup", onDragEnd);
      document.removeEventListener("pointercancel", onDragEnd);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, [dragging, dragStartX, width, containerElement, setWidth]);

  useEffect(() => {
    if (width >= window.innerWidth || dragging) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [width, dragging]);

  if (!isUserAuthenticated) {
    return null;
  }

  const containerBase =
    "h-screen bg-indigo-600 p-4 flex flex-col font-companion text-md shrink-0 z-panel";
  const positionClass = "sticky top-0";
  const transitionClass =
    !dragging && "transition-[width] duration-150 ease-out";

  return (
    <>
      <CompanionModalContainerProvider value={containerElement}>
        <div
          ref={setContainerElement}
          className={`${containerBase} ${positionClass} ${transitionClass} relative ${isCollapsed ? "cursor-pointer" : ""}`}
          style={{ width: `${width}px` }}
          onClick={
            isCollapsed
              ? () => setWidth(getDefaultWidth())
              : undefined
          }
          data-testid="companion-sidebar"
        >
          <div className="flex flex-wrap justify-between items-center gap-y-2">
            {isCollapsed ? (
              <div
                className="absolute top-2 left-2 z-50 flex flex-col items-center gap-1"
                data-testid="companion-sidebar-heads"
              >
                {[
                  AvailableCompanions.OTTO,
                  AvailableCompanions.QUILL,
                  AvailableCompanions.SUMMI,
                  AvailableCompanions.SAGE,
                ].map((c) => (
                  <img
                    key={c.name}
                    src={c.headImage}
                    alt={c.displayName}
                    width="47"
                    height="47"
                    data-testid={`companion-sidebar-head-${c.name}`}
                  />
                ))}
              </div>
            ) : (
              <>
                <div className="flex flex-row items-center gap-2">
                  {isNarrow ? (
                    <img
                      src={CompanionIcon}
                      alt="Companion"
                      className="h-12 w-12 object-contain shrink-0"
                      data-testid="companion-sidebar-logo-icon"
                    />
                  ) : (
                    <img
                      src={CompanionLogo}
                      width="140px"
                      className="py-2"
                    />
                  )}
                  <TreatBalanceIndicator
                    onClick={handleTreatsClick}
                  />
                </div>
                <div className="flex flex-wrap gap-xs items-center justify-end ml-auto">
                  {companionAvailable && (
                    <CompanionNavButton
                      icon={faMessageSmile}
                      title="Active Chat"
                      isTabActive={
                        activeTab === AvailableCompanionTabs.chat
                      }
                      onClick={() => {
                        setCompanionSession(null);
                        setActiveTab(AvailableCompanionTabs.chat);
                      }}
                      data-testid="companion-active-session"
                    />
                  )}
                  {companionAvailable && (
                    <CompanionNavButton
                      icon={faHistory}
                      title="Chat History"
                      isTabActive={
                        activeTab === AvailableCompanionTabs.history
                      }
                      onClick={() =>
                        setActiveTab(AvailableCompanionTabs.history)
                      }
                      data-testid="companion-session-history"
                    />
                  )}
                  {showNotificationsButton && (
                    <CompanionNavButton
                      icon={faBell}
                      title="Notifications"
                      isTabActive={isNotificationsTabActive}
                      onClick={() =>
                        setActiveTab(
                          AvailableCompanionTabs.notifications,
                        )
                      }
                      badgeCount={companionUnreadCount}
                      data-testid="companion-notifications-button"
                    />
                  )}
                  <CompanionNavButton
                    icon={faGear}
                    title="Settings"
                    isTabActive={
                      activeTab === AvailableCompanionTabs.settings
                    }
                    onClick={() =>
                      setActiveTab(AvailableCompanionTabs.settings)
                    }
                    data-testid="companion-settings-button"
                  />
                  <CompanionNavButton
                    icon={faQuestion}
                    title="Help"
                    isTabActive={
                      activeTab === AvailableCompanionTabs.help
                    }
                    onClick={() =>
                      setActiveTab(AvailableCompanionTabs.help)
                    }
                  />
                </div>
              </>
            )}
          </div>

          <CompanionTooltips />

          <div
            className={`${
              isCollapsed && "invisible max-w-0"
            } flex flex-col flex-1 relative overflow-hidden`}
          >
            <div className="overflow-auto flex-1 w-full">
              {activeTab === AvailableCompanionTabs.chat &&
                (!companionAvailable ? (
                  <CompanionDownstate />
                ) : !companionSession?.companionSessionUuid ? (
                  <CompanionV2Chat />
                ) : (
                  <CompanionSessionChat />
                ))}
              {activeTab === AvailableCompanionTabs.history &&
                (!companionAvailable ? (
                  <CompanionDownstate />
                ) : (
                  <CompanionSessionHistory />
                ))}
              {activeTab === AvailableCompanionTabs.notifications && (
                <CompanionNotifications />
              )}
              {activeTab === AvailableCompanionTabs.settings && (
                <CompanionSettings />
              )}
              {activeTab === AvailableCompanionTabs.help && (
                <CompanionHelp />
              )}
            </div>
          </div>

          <div
            className={`flex ${isCollapsed && "invisible max-w-0"}`}
          >
            {companionAvailable && <NewSessionButton />}
          </div>

          <div
            ref={handleRef}
            onPointerDown={onHandlePointerDown}
            onClick={(e) => {
              e.stopPropagation();
              if (dragOccurredRef.current) return;
              if (isCollapsed) {
                setWidth(getDefaultWidth());
              } else {
                setWidth(SIDEBAR_COLLAPSED_WIDTH);
              }
            }}
            className="absolute -right-1 top-0 h-full cursor-col-resize select-none touch-none flex items-center justify-center z-55 w-4"
            data-testid="companion-resize-handle"
          >
            <div className="mr-2 ">
              <FontAwesomeIcon
                icon={faGripLinesVertical}
                className={`text-white text-xl ${
                  isCollapsed && "animate-pulse"
                } `}
              />
            </div>
          </div>
        </div>
      </CompanionModalContainerProvider>
    </>
  );
};
